import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';

import { pageStructureSchema, webNormalize } from '@web/store/page-structure/page-structure/mockStructureData';
import { Widgets } from '@common/enums';

import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { CredentialsFacade } from '@web/store/credentials/credentials.facade';
import { NotifyFacade } from '@common/utils/notify/notify.facade';
import { TabsFacade } from '@web/store/page-structure/tabs/tabs.facade';
import { ContentFacade } from '@web/store/website/content/content.facade';

import { WebStructureAdapterService } from '@web/core/services/web-structure-adapter.service';

import { PageStructureService } from '@web/store/page-structure/page-structure/page-structure.service';

import PageStructureActions from './page-structure.actions';
import { splitUnmigratedBlocks } from './unmigrated-blocks';

@Injectable()
export class PageStructureEffect {
    private readonly actions$ = inject(Actions);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly sectionsFacade = inject(SectionsFacade);
    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly gridsFacade = inject(GridsFacade);
    private readonly tabsFacade = inject(TabsFacade);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly credentialsFacade = inject(CredentialsFacade);
    private readonly structureAdapterService = inject(WebStructureAdapterService);
    private readonly contentFacade = inject(ContentFacade);
    private readonly notifyFacade = inject(NotifyFacade);
    private readonly translateService = inject(TranslateService);

    private readonly setPageStructure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(PageStructureActions.setPageStructure),
                map(async ({ structure }) => {
                    this.contentFacade.setIsLoading(true);

                    // Copied before the adapter runs: its widget adapters may rewrite data in place, and
                    // a block the canvas cannot take has to be written back exactly as it was read.
                    const persisted = structuredClone(structure);
                    const adapted = this.structureAdapterService.adaptWebStructureBeforeSet(structure);
                    const { editable, unmigrated } = splitUnmigratedBlocks(
                        persisted,
                        adapted.structure,
                        adapted.failedBlockIndexes
                    );

                    const { blocks, sections, columns, widgets } = webNormalize(editable, pageStructureSchema);

                    try {
                        const updatedWidgets = await this.structureAdapterService.updateWidgetsData(widgets);

                        this.blocksFacade.init(blocks);
                        this.sectionsFacade.init(sections);
                        this.columnsFacade.init(columns);
                        this.widgetsFacade.init(
                            updatedWidgets.filter((item) => PageStructureService.isEntityWidget(item))
                        );
                        this.gridsFacade.init(
                            updatedWidgets.filter((item) => PageStructureService.isEntityGrid(item)) as any
                        ); // TODO: fix typing
                        this.tabsFacade.init(
                            updatedWidgets.filter((item) => PageStructureService.isEntityTab(item)) as any
                        ); // TODO: fix typing

                        const mapsApiKey =
                            updatedWidgets.find((widget) => widget.name === Widgets.MAP)?.data?.apiKey || '';
                        this.credentialsFacade.setMapsApiKey(mapsApiKey);
                    } catch (error) {
                        this.blocksFacade.init(blocks);
                        this.sectionsFacade.init(sections);
                        this.columnsFacade.init(columns);
                        this.widgetsFacade.init(widgets.filter((item) => PageStructureService.isEntityWidget(item)));
                        this.gridsFacade.init(widgets.filter((item) => PageStructureService.isEntityGrid(item)));
                        this.tabsFacade.init(widgets.filter((item) => PageStructureService.isEntityTab(item)));

                        const mapsApiKey = widgets.find((widget) => widget.name === Widgets.MAP)?.data?.apiKey || '';
                        this.credentialsFacade.setMapsApiKey(mapsApiKey);
                    } finally {
                        // Next to the blocks, with nothing awaited in between: a save takes both in the same
                        // read, and must never pair one page's blocks with another page's leftovers.
                        this.contentFacade.setUnmigratedBlocks(unmigrated);
                        this.contentFacade.setIsLoading(false);
                    }

                    if (adapted.failedBlockIndexes.size) {
                        this.notifyFacade.notifyAction(
                            this.translateService.instant('page_blocks_not_loaded', {
                                count: adapted.failedBlockIndexes.size,
                            }),
                            'warning'
                        );
                    }
                })
            ),
        { dispatch: false }
    );
}
